import React, { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { convertCurrency } from "api";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { roundUp } from "helpers/commonHelpers";
import CostFormCalculationContext from "./CostCalculationContext";
import { QuotationFormActionType, QuotationFormAction } from "../../QuotationFormState";

type CostCalculationProviderProps = {
  children: React.ReactNode;
  dispatch: Dispatch<QuotationFormAction>;
};

const DEFAULT_CURRENCY = "USD";

function CostCalculationProvider(props: CostCalculationProviderProps) {
  const { children, dispatch } = props;
  const { id } = useParams();
  const { data: quoteRequestDetails } = useGetQuoteRequestDetails({
    shipmentId: id!,
    options: { enabled: !!id, refetchOnMount: false },
  });
  const shipperPreferredCurrency = quoteRequestDetails?.organization?.shipment_currency || DEFAULT_CURRENCY;

  const { getValues } = useFormContext();
  const [conversionRates, setConversionRates] = useState<any>({});
  const [calculatingFreightCharges, setCalculatingFreightCharges] = useState(false);
  const [calculatingOtherCharges, setCalculatingOtherCharges] = useState(false);
  const [totalFreightCharges, setTotalFreightCharges] = useState(0);
  const [totalOtherCharges, setTotalOtherCharges] = useState(0);
  const convertCurrencyFreightAbortController = useRef<AbortController | null>(null);
  const convertCurrencyOtherAbortController = useRef<AbortController | null>(null);

  const currentChargeFn: any = {
    main_freight_charges: {
      setLoading: setCalculatingFreightCharges,
      setTotal: setTotalFreightCharges,
      abortController: convertCurrencyFreightAbortController,
    },
    other_charges: {
      setLoading: setCalculatingOtherCharges,
      setTotal: setTotalOtherCharges,
      abortController: convertCurrencyOtherAbortController,
    },
  };

  useEffect(() => {
    if (totalFreightCharges || totalOtherCharges) {
      dispatch({
        type: QuotationFormActionType.UPDATE_TOTAL_AMOUNT,
        payload: `${roundUp(+roundUp(totalFreightCharges) + +roundUp(totalOtherCharges))} ${shipperPreferredCurrency}`,
      });
    }
  }, [totalFreightCharges, totalOtherCharges, shipperPreferredCurrency]);

  const getCurrenciesToFetch = (freightCharges: any) => [
    ...new Set(
      freightCharges
        .filter(
          (charge: any) =>
            charge.amount &&
            charge.quantity &&
            charge.currency_code &&
            charge.currency_code !== shipperPreferredCurrency &&
            !conversionRates[`${charge.currency_code}-${shipperPreferredCurrency}`],
        )
        .map((item: any) => item.currency_code),
    ),
  ];

  const fetchCurriencyRates = async (chargeName: string, currienciesToFetch: any) => {
    if (currentChargeFn[chargeName].abortController?.current?.abort) {
      currentChargeFn[chargeName].abortController.current.abort();
    }
    currentChargeFn[chargeName].abortController.current = new AbortController();
    currentChargeFn[chargeName].setLoading(true);
    const convertedCurrencies: any = await Promise.all(
      currienciesToFetch.map((currency: any) =>
        convertCurrency({
          params: {
            currency,
            new_currency: shipperPreferredCurrency,
            value: 1,
          },
          signal: currentChargeFn[chargeName].abortController?.current?.signal,
        })
          .then((res) => Promise.resolve(res.data))
          .catch((err) => Promise.reject(err)),
      ),
    );
    currentChargeFn[chargeName].setLoading(false);
    return convertedCurrencies;
  };

  const calculateTotalCost = useCallback(
    async (chargeName: string) => {
      const currentConversionRates: any = { ...conversionRates };
      const charges = getValues(`costs.${chargeName}`) || [];
      const currienciesToFetch = getCurrenciesToFetch(charges);
      if (currienciesToFetch.length) {
        const convertedCurrencies = await fetchCurriencyRates(chargeName, currienciesToFetch);
        convertedCurrencies.forEach((item: any) => {
          currentConversionRates[`${item.currency}-${item.new_currency}`] = item;
        });
        setConversionRates((prev: any) => ({
          ...prev,
          ...currentConversionRates,
        }));
      }

      let totalCostInShipperCurrency = 0;
      charges.forEach((charge: any) => {
        if (charge.amount && charge.quantity && charge.currency_code) {
          const currentItemCost = +charge.amount * +charge.quantity;
          if (charge.currency_code === shipperPreferredCurrency) {
            totalCostInShipperCurrency += currentItemCost;
          } else {
            const currentItemCostInShipperCurrency = +(
              currentItemCost *
              (currentConversionRates[`${charge.currency_code}-${shipperPreferredCurrency}`]?.conversion_rate || 1)
            );
            totalCostInShipperCurrency += currentItemCostInShipperCurrency;
          }
        }
      });
      currentChargeFn[chargeName].setTotal(totalCostInShipperCurrency);
    },
    [conversionRates, shipperPreferredCurrency],
  );

  const getConversionRates = useCallback(() => {
    const allCharges = [...(getValues(`costs.main_freight_charges`) || []), ...(getValues("costs.other_charges") || [])];
    const currienciesToShow = [
      ...new Set(
        allCharges
          .filter(
            (charge: any) =>
              charge.amount && charge.quantity && charge.currency_code && charge.currency_code !== shipperPreferredCurrency,
          )
          .map((item: any) => item.currency_code),
      ),
    ];
    return currienciesToShow
      .map((currency: string) => conversionRates[`${currency}-${shipperPreferredCurrency}`])
      .filter(Boolean);
  }, [conversionRates, shipperPreferredCurrency]);

  const formCalculationValue = useMemo(
    () => ({
      calculateTotalCost,
      getConversionRates,
      calculatingFreightCharges,
      calculatingOtherCharges,
      totalFreightCharges,
      totalOtherCharges,
      shipperPreferredCurrency,
    }),
    [
      calculateTotalCost,
      getConversionRates,
      calculatingFreightCharges,
      calculatingOtherCharges,
      totalFreightCharges,
      totalOtherCharges,
      shipperPreferredCurrency,
    ],
  );
  return <CostFormCalculationContext.Provider value={formCalculationValue}>{children}</CostFormCalculationContext.Provider>;
}

export default CostCalculationProvider;
